import { Edit24, Add24, View24 } from "@carbon/icons-react";
import React, { useContext, useEffect } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SubmitButton from "../../components/Buttons/SubmitButton";
import Card from "../../components/Card/Card";
import { AuthContext } from "../../contexts/AuthContext";
import { Loading } from "carbon-components-react";
import http from "../../http-common";
import { toast } from "react-toastify";

// "NUR" (Janela 1) or "NU2" (Janela 2) - must match the backend's
// BusinessPlanService#windowFromNur prefix convention.
const nurPrefixFor = (windowCode) => (windowCode === 2 ? "NU2" : "NUR");

const BusinessPlanSection = ({ windowLabel, windowCode, businessPlan, navigate }) => (
  <div style={{ marginTop: "1.5rem" }}>
    <h5>Plano de Negócios ({windowLabel})</h5>
    <p>
      {businessPlan
        ? "Você já possui um plano de negócios registado."
        : "Registe o seu plano de negócios."}
    </p>

    {businessPlan ? (
      <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
        {businessPlan.currentStatusCode === 1 ? (
          <SubmitButton
            type="button"
            className="emprega-bg-orange"
            label="Continuar Edição do Plano"
            icon={Edit24}
            onClick={() => navigate("../editar-plano-de-negocio/" + businessPlan.uuid)}
          />
        ) : (
          <SubmitButton
            type="button"
            className="emprega-bg-blue"
            label="Ver Plano de Negócios"
            icon={View24}
            onClick={() => navigate("../plano-de-negocio/" + businessPlan.uuid)}
          />
        )}
      </div>
    ) : (
      <div style={{ marginTop: "1rem" }}>
        <SubmitButton
          type="button"
          className="emprega-bg-green"
          label="Criar Plano de Negócios"
          icon={Add24}
          onClick={() => navigate(`../registar-plano-de-negocio?window=${windowCode}`)}
        />
      </div>
    )}
  </div>
);

const CompetitorDashboardPage = () => {
  const { user } = useContext(AuthContext);
  const [uuid, setUuid] = useState(null);
  const [competitor, setCompetitor] = useState(null);
  const [businessPlan, setBusinessPlan] = useState(null);
  const [window2BusinessPlan, setWindow2BusinessPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEligible, setIsEligible] = useState(false);
  const navigate = useNavigate();

  const initializeDashboard = async () => {
    try {
      setIsLoading(true);

      // Step 1: Get candidature UUID
      try {
        const candidatureResponse = await http.get(
          `/applications/uuid-by-phone-number/${user.username}`
        );
        setUuid(candidatureResponse.data);
      } catch (error) {
        console.log("No candidature found");
      }

      // Step 2: Check competitor eligibility
      try {
        const competitorResponse = await http.get(
          `/business-plan/competitor/${user.username}`
        );

        if (competitorResponse.data) {
          const competitorData = competitorResponse.data;
          setCompetitor(competitorData);

          if (competitorData.isEligible) {
            setIsEligible(true);

            // Step 3: Check for existing Janela 1 business plan
            try {
              const businessPlanResponse = await http.get(
                `/business-plan/nur/${nurPrefixFor(1)}${user.username}`
              );

              if (businessPlanResponse.data) {
                setBusinessPlan(businessPlanResponse.data);
              }
            } catch (error) {
              console.log("No business plan found");
            }
          }

          // Step 4: if the admin has enabled this candidate for Janela 2,
          // check for an existing Janela 2 business plan (same candidature,
          // second plan).
          if (competitorData.eligibleForWindow2) {
            try {
              const window2Response = await http.get(
                `/business-plan/nur/${nurPrefixFor(2)}${user.username}`
              );

              if (window2Response.data) {
                setWindow2BusinessPlan(window2Response.data);
              }
            } catch (error) {
              console.log("No Janela 2 business plan found");
            }
          }
        }
      } catch (error) {
        console.log("Competitor not found or not eligible");
      }
    } catch (error) {
      console.error("Error initializing dashboard:", error);
      toast.error("Erro ao carregar dados do painel");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.username) {
      initializeDashboard();
    }
  }, [user]);

  // Show loading state while checking eligibility and business plan status
  if (isLoading) {
    return (
      <Card title={"Olá " + user.firstName + " " + user.lastName}>
        <Loading
          active={isLoading}
          description="Carregando informações do painel..."
          withOverlay={true}
        />
      </Card>
    );
  }

  return (
    <Card title={"Olá " + user.firstName + " " + user.lastName}>
      <div>
        <h5>O período de candidaturas terminou</h5>

        {/* Show candidature button if UUID exists */}
        {uuid && (
          <div style={{ marginBottom: "2rem" }}>
            <SubmitButton
              type="button"
              className="mt-2 emprega-bg-green"
              label="Consultar Candidatura"
              icon={Edit24}
              onClick={() => navigate("../candidature/" + uuid)}
            />
          </div>
        )}

        {/* Show business plan section(s) if competitor is eligible */}
        {isEligible && (
          <BusinessPlanSection
            windowLabel="Janela 1"
            windowCode={1}
            businessPlan={businessPlan}
            navigate={navigate}
          />
        )}

        {/* A candidate keeps their single candidature - Janela 2 only adds a
            second business plan, once the admin has marked them eligible. */}
        {competitor?.eligibleForWindow2 && (
          <BusinessPlanSection
            windowLabel="Janela 2"
            windowCode={2}
            businessPlan={window2BusinessPlan}
            navigate={navigate}
          />
        )}

        {/* Show message if competitor is not eligible */}
        {!isEligible && (
          <div
            style={{
              marginTop: "2rem",
              padding: "1rem",
              backgroundColor: "#f4f4f4",
              borderRadius: "4px",
            }}
          >
            <h4>Plano de Negócios</h4>
            <br />
            <h6>Caro/a Empreendedor/a</h6>
            <br />
            <p>
              Agradecemos por tentar aceder ao módulo de submissão dos planos de
              negócio no âmbito do Conecta Negócios, na componente de Ligações
              de Consumo.
            </p>

            <p>
              Informamos que são elegíveis para submeter os planos de negócios
              na{" "}
              <strong>
                presente Janela de submissão (Janela 1) os/as empreendedores/as
                que até ao dia 23 de Outubro de 2025 concluíram as seguintes
                formações:
              </strong>
            </p>

            <ol>
              <li>
                <strong>Iniciativa Pessoal</strong>;
              </li>
              <li>
                <strong>Gestão Operacional</strong>;
              </li>
            </ol>

            <p className="mt-2">
              Serão elegíveis para submeter planos de negócios na Janela 2 os/as
              empreendedores/as que até ao dia{" "}
              <strong>23 de Outubro de 2025</strong> tenham concluído a formação
              em <strong>Iniciativa Pessoal</strong>, porém ainda não concluíram
              a formação em <strong>Gestão Operacional</strong>. Caso este seja
              o seu,{" "}
              <strong>
                por favor, certifique-se de ao ser convocado/a a participar da
                formação em Gestão Operacional, participe e conclua a mesma
              </strong>{" "}
              por forma a ser elegível para a Janela 2.
            </p>
            <br />
            <p>
              <strong>
                Caso não tenha concluído nenhuma das duas formações, lamentamos
                informar que não poderá ser elegível a participar das janelas de
                submissão de planos de negócio de acordo com as regras desta
                competição.
              </strong>{" "}
              No entanto, <strong>encorajamos a não desanimar</strong> e
              participar de forma activa nas futuras oportunidades.
            </p>
            <br />
          </div>
        )}
      </div>
    </Card>
  );
};

export default CompetitorDashboardPage;
